import React, { FunctionComponent } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { Compound, Substance } from '../../shared/models/Substance';
import { makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { Box, TablePagination } from '@material-ui/core';
import LinearProgress from '@material-ui/core/LinearProgress';
import { SubstanceModal } from '../SubstanceModal/SubstanceModal';
import { krajowaBazaTheme as theme } from '../../config/themes';
import { InfoTooltip } from '../InfoTooltip/InfoTooltip';
import { CompoundModal } from '../CompoundModal/CompoundModal';

interface SubstancesTableProps {
    data: Array<Substance | Compound>;
    onPageSizeChange?: (size: number) => any;
    onSetPage?: (page: number) => any;
    allRows: number;
    pageSize: number;
    page: number;
    loading?: boolean;
    noResultsMessage?: () => React.ReactNode;
}

interface ThemeWithProgress extends Theme {
    progress: boolean;
}

const useStyles = makeStyles<ThemeWithProgress>(({ progress }) => {
    return {
        table: {
            minWidth: 650,
        },
        trap: {
            height: 10,
            width: '100%',
        },
        progress: {
            visibility: progress ? 'visible' : 'hidden',
        },
        flex: {
            display: 'flex',
            justifyContent: 'center',
        },
        centered: {
            textAlign: 'center',
        },
    };
});

export function parseFormula(formula: string, inBrackets: boolean = false) {
    if (formula === undefined) return '';
    let re: RegExp = /\d+/g;
    if (inBrackets) re = /(?<=\(.*)(?:\d+)(?=\w*\))/g;
    //@ts-ignore
    const digits: RegExpExecArray[] = [...new Set(formula.matchAll(re))];
    let outStr = formula;

    digits.forEach((d, index) => {
        outStr =
            outStr.substring(0, d.index + index * 11) +
            `<sub>${d[0]}</sub>` +
            outStr.substring(d.index + d[0].length + index * 11); //'<sub></sub>'.length = 11
    });
    return outStr;
}

const StyledTableHeaderCell = withStyles((theme) => ({
    head: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        textAlign: 'center',
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const StyledTableCell = withStyles((theme) => ({
    body: {
        border: 'none',
    },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
    root: {
        '&:nth-of-type(odd)': {
            backgroundColor: 'rgb(241 249 253)',
        },
    },
}))(TableRow);

export const SubstancesTable: FunctionComponent<SubstancesTableProps> = ({
    data,
    pageSize,
    page,
    allRows,
    onPageSizeChange,
    onSetPage,
    loading,
    noResultsMessage,
}) => {
    const classes = useStyles({ ...theme, progress: loading });
    const [t] = useTranslation();
    return (
        <div>
            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={allRows}
                rowsPerPage={pageSize}
                page={page}
                onChangePage={(val, page) => {
                    if (onSetPage) onSetPage(page);
                }}
                onChangeRowsPerPage={(ev) => {
                    if (onPageSizeChange)
                        onPageSizeChange(Number(ev.target.value));
                }}
            />
            <LinearProgress
                classes={{
                    root: classes.progress,
                }}
            />
            <TableContainer component={Paper}>
                <Table className={classes.table} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <StyledTableHeaderCell>
                                <div className={classes.flex}>
                                    {t('CAS')}
                                    <InfoTooltip
                                        title={t('CAS info').toString()}
                                    />
                                </div>
                            </StyledTableHeaderCell>
                            <StyledTableHeaderCell>
                                {t('Name')}
                            </StyledTableHeaderCell>
                            <StyledTableHeaderCell>
                                {t('Other names')}
                            </StyledTableHeaderCell>
                            <StyledTableHeaderCell>
                                <div className={classes.flex}>
                                    {t('KB name')}
                                    <InfoTooltip
                                        title={t('KB name info').toString()}
                                    />
                                </div>
                            </StyledTableHeaderCell>
                            <StyledTableHeaderCell>
                                {t('Action')}
                            </StyledTableHeaderCell>
                            {/* <StyledTableHeaderCell>{t('Formula')}</StyledTableHeaderCell> */}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row: Substance | Compound) => (
                            <StyledTableRow key={row.name + row.id}>
                                <StyledTableCell
                                    component="th"
                                    scope="row"
                                    className={classes.casCell}
                                >
                                    {(row as Substance).casNumber}
                                </StyledTableCell>
                                <StyledTableCell component="td">
                                    {row.name}
                                </StyledTableCell>
                                <StyledTableCell component="td">
                                    {(row as Substance).otherNames?.join(', ')}
                                </StyledTableCell>
                                <StyledTableCell component="td">
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: parseFormula(
                                                (row as Substance).kbName,
                                                true,
                                            ),
                                        }}
                                    />
                                </StyledTableCell>
                                <StyledTableCell
                                    component="td"
                                    className={classes.centered}
                                >
                                    {(row as Compound).components ===
                                    undefined ? (
                                        <SubstanceModal
                                            substance={row as Substance}
                                        />
                                    ) : (
                                        <CompoundModal
                                            compound={row as Compound}
                                        />
                                    )}
                                </StyledTableCell>
                                {/* <StyledTableCell component="td">
                                    <span dangerouslySetInnerHTML={{ __html: parseFormula(row.formula) }}/>
                                </StyledTableCell> */}
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {!data.length && noResultsMessage && noResultsMessage()}
            <LinearProgress
                classes={{
                    root: classes.progress,
                }}
            />
            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={allRows}
                rowsPerPage={pageSize}
                page={page}
                onChangePage={(val, page) => {
                    if (onSetPage) onSetPage(page);
                }}
                onChangeRowsPerPage={(ev) => {
                    if (onPageSizeChange)
                        onPageSizeChange(Number(ev.target.value));
                }}
            />
        </div>
    );
};
